/**
 * Dependency graph: tasks as cards in columns (longest-path layer), solid arrows
 * for predecessor → successor, dashed lines for parent → sub-task.
 */
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { layoutGraph, taskProgress, isBlocked } from "@/lib/tasks";
import type { ProjectData, Task } from "@/lib/store";
import { ProgressRing, TaskStatusIcon } from "./TaskProps";
import { Avatar } from "./PropertyIcons";

const W = 220;
const H = 68;
const GX = 80;
const GY = 16;

export function TaskGraph({ d, tasks }: { d: ProjectData; tasks: Task[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { nodes, edges, cols, rows } = layoutGraph(d, tasks);
  const pos = new Map(nodes.map((n) => [n.task.id, { x: 24 + n.col * (W + GX), y: 24 + n.row * (H + GY) }]));
  const width = 48 + Math.max(1, cols) * (W + GX);
  const height = 48 + Math.max(1, rows) * (H + GY);
  const hasEdges = edges.length > 0;

  return (
    <div className="relative flex-1 overflow-auto">
      {!hasEdges && (
        <div className="pointer-events-none absolute left-1/2 top-4 -translate-x-1/2 rounded-md border border-border bg-elevated px-3 py-1.5 text-[12px] text-muted">{t("graph.hint")}</div>
      )}
      <div className="relative" style={{ width, height }}>
        <svg className="absolute inset-0" width={width} height={height}>
          <defs>
            <marker id="garrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse"><path d="M0 0L10 5L0 10z" fill="var(--accent)" /></marker>
          </defs>
          {edges.map((e) => {
            const a = pos.get(e.from);
            const b = pos.get(e.to);
            if (!a || !b) return null;
            if (e.kind === "parent") {
              const x1 = a.x + W / 2, y1 = a.y + H, y2 = b.y;
              const path = a.x === b.x ? `M${x1} ${y1} V${y2}` : `M${a.x + W} ${a.y + H - 8} C${a.x + W + 30} ${a.y + H - 8} ${b.x - 30} ${b.y + 8} ${b.x} ${b.y + 8}`;
              return <path key={`p${e.from}${e.to}`} d={path} fill="none" stroke="var(--text-faint)" strokeWidth="1.2" strokeDasharray="4 3" />;
            }
            const x1 = a.x + W, y1 = a.y + H / 2, x2 = b.x, y2 = b.y + H / 2;
            const c = Math.max(30, (x2 - x1) / 2);
            return <path key={`d${e.from}${e.to}`} d={`M${x1} ${y1} C${x1 + c} ${y1} ${x2 - c} ${y2} ${x2} ${y2}`} fill="none" stroke="var(--accent)" strokeWidth="1.6" markerEnd="url(#garrow)" />;
          })}
        </svg>
        {nodes.map((n) => {
          const p = pos.get(n.task.id)!;
          const prog = taskProgress(d, n.task);
          const blocked = isBlocked(d, n.task.id);
          const lead = n.task.lead_id ? d.users[n.task.lead_id] : undefined;
          return (
            <button
              key={n.task.id}
              className="absolute flex flex-col justify-between rounded-lg border border-border bg-elevated p-2.5 text-left shadow-sm hover:border-accent"
              style={{ left: p.x, top: p.y, width: W, height: H, borderLeft: `3px solid ${n.task.color ?? "var(--accent)"}` }}
              onClick={() => void navigate({ to: "/$ws/task/$id", params: { ws: d.slug, id: n.task.id } })}
            >
              <div className="flex items-center gap-1.5 text-[13px]">
                <TaskStatusIcon status={n.task.status} />
                <span className="truncate font-medium">{n.task.name}</span>
              </div>
              <div className="flex items-center gap-2 text-[11.5px] text-muted">
                <ProgressRing ratio={prog?.ratio ?? null} size={14} />
                <span>{prog ? `${Math.round(prog.ratio * 100)}%` : "—"}</span>
                {n.task.parent_id && <span className="text-faint">↳ {d.tasks[n.task.parent_id]?.name}</span>}
                {blocked && <span className="rounded bg-warning/20 px-1 text-warning">{t("task.blocked")}</span>}
                <span className="ml-auto"><Avatar user={lead} size={16} /></span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
